import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import axios from 'axios';
import { db } from '../firebase.js';
import { collection, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const EBayOAuth = () => {
  const params = useParams();
  const [authCode, setAuthCode] = useState('');
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const exchangeCode = async (code) => {
    try {
      const response = await axios.post(process.env.REACT_APP_EBAY_TOKEN_REFRESH, {
        code,
        grant_type: 'authorization_code',
        redirect_uri: process.env.REACT_APP_EBAY_REDIRECT_URI,
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      await saveTokens(response.data);
    } catch (error) {
      console.error('Error exchanging code:', error);
      setErrorMessage('Failed to connect eBay account. Please try again.');
      setStatus('error');
    }
  };

  const saveTokens = async (responseData) => {
    const custId = localStorage.getItem('userId');
    const destId = localStorage.getItem('destId');
    const accessToken = responseData.access_token;
    const refreshToken = responseData.refresh_token;

    const custDocRef = doc(collection(db, 'customers'), custId);
    const cdSnap = await getDoc(custDocRef);
    const data = cdSnap.data();
    const destinations = data.destinations;
    const foundIndex = destinations.findIndex((item) => item.id === destId);

    if (foundIndex !== -1) {
      destinations[foundIndex].accessToken = accessToken;
      destinations[foundIndex].storeKey = refreshToken;
      data.destinations = destinations;
      try {
        await updateDoc(custDocRef, data);
        setStatus('success');
        window.location.href = 'shopik://accountsettings/success';
        setTimeout(() => {
          window.location.href = '/settings';
        }, 1500);
      } catch (error) {
        console.error('Error saving tokens:', error);
        setErrorMessage('Account connected but failed to save. Please try again.');
        setStatus('error');
      }
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code == null) {
      // Initiate OAuth — redirect to eBay
      localStorage.setItem('userId', params['customer']);
      localStorage.setItem('destId', params['destination']);
      window.location.href =
        process.env.REACT_APP_EBAY_AUTH_URL +
        '?client_id=' + process.env.REACT_APP_EBAY_CLIENT_ID +
        '&response_type=code' +
        '&redirect_uri=' + process.env.REACT_APP_EBAY_REDIRECT_URI +
        '&scope=https://api.ebay.com/oauth/api_scope https://api.ebay.com/oauth/api_scope/sell.marketing.readonly https://api.ebay.com/oauth/api_scope/sell.marketing https://api.ebay.com/oauth/api_scope/sell.inventory.readonly https://api.ebay.com/oauth/api_scope/sell.inventory https://api.ebay.com/oauth/api_scope/sell.account.readonly https://api.ebay.com/oauth/api_scope/sell.account https://api.ebay.com/oauth/api_scope/sell.fulfillment.readonly https://api.ebay.com/oauth/api_scope/sell.fulfillment https://api.ebay.com/oauth/api_scope/sell.analytics.readonly https://api.ebay.com/oauth/api_scope/sell.finances https://api.ebay.com/oauth/api_scope/sell.payment.dispute https://api.ebay.com/oauth/api_scope/sell.reputation https://api.ebay.com/oauth/api_scope/sell.reputation.readonly https://api.ebay.com/oauth/api_scope/commerce.notification.subscription https://api.ebay.com/oauth/api_scope/commerce.notification.subscription.readonly https://api.ebay.com/oauth/api_scope/sell.stores https://api.ebay.com/oauth/api_scope/sell.stores.readonly';
    } else {
      setAuthCode(code);
      exchangeCode(code);
    }
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
        textAlign: 'center',
        px: 3,
      }}
    >
      <img src="/images/logo.png" alt="Hemlock" style={{ maxWidth: '160px', marginBottom: 8 }} />

      {status === 'loading' && (
        <>
          <CircularProgress sx={{ color: '#4751ed', mt: 1 }} />
          <Typography variant="body1" color="text.secondary">
            Connecting your eBay account…
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#4751ed' }} />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            eBay account connected
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Redirecting you back to Settings…
          </Typography>
          <Button
            variant="text"
            href="/settings"
            sx={{ color: '#4751ed', textTransform: 'none', mt: 1 }}
          >
            Click here if not redirected
          </Button>
        </>
      )}

      {status === 'error' && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 360 }}>
            {errorMessage}
          </Typography>
          <Button
            variant="contained"
            href="/settings"
            sx={{
              mt: 1,
              backgroundColor: '#4751ed',
              textTransform: 'none',
              ':hover': { backgroundColor: '#6133aa' },
            }}
          >
            Back to Settings
          </Button>
        </>
      )}
    </Box>
  );
};

export default EBayOAuth;
